import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, UploadCloud } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { useSite } from "../../context/SiteContext";

const SLOTS = [
  { key: "hero", label: "Homepage Hero", hint: "Large landscape photo shown at the top of the homepage" },
  { key: "renovation", label: "Renovations Service", hint: "Shown beside the Renovations block and page" },
  { key: "deck", label: "Extensions Service", hint: "Shown beside Alterations & Extensions and the decks card" },
  { key: "newbuild", label: "New Builds Service", hint: "Shown beside New Builds and the repairs card" },
  { key: "kitchen", label: "Kitchen / Bathroom", hint: "Used for kitchen & bathroom imagery" },
  { key: "consult", label: "About Teaser (Homepage)", hint: "The 'Built on Experience' section photo" },
  { key: "builder", label: "About Page / Team", hint: "Shown on the About page and urgent work card" },
];

export default function ImagesTab() {
  const { images, customImages = [] } = useSite();
  const [busy, setBusy] = useState(null);
  const [bumps, setBumps] = useState({});
  const [custom, setCustom] = useState(null);
  const inputRefs = useRef({});
  const isCustom = (key) => (custom === null ? customImages : custom).includes(key);

  const upload = async (key, file) => {
    if (!file) return;
    setBusy(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/images/${key}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBumps((b) => ({ ...b, [key]: Date.now() }));
      setCustom((c) => [...new Set([...(c === null ? customImages : c), key])]);
      toast.success("Image updated — the website will use the new photo");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const srcFor = (key) => {
    const base = images[key];
    if (!base) return "";
    const sep = base.includes("?") ? "&" : "?";
    return bumps[key] ? `${base}${sep}v2=${bumps[key]}` : base;
  };

  return (
    <div>
      <h2 className="font-display font-bold text-xl text-ink mb-1">Website Images</h2>
      <p className="text-xs text-slate2 mb-6">Replace any photo on the website with your own. Photos are automatically resized and optimised — landscape photos work best.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="images-slot-grid">
        {SLOTS.map((slot) => (
          <div key={slot.key} className="bg-white border border-line" data-testid={`image-slot-${slot.key}`}>
            <div className="aspect-[16/9] bg-charcoal/5 overflow-hidden">
              <img src={srcFor(slot.key)} alt={slot.label} className="w-full h-full object-cover" />
            </div>
            <div className="p-4">
              <p className="font-display font-semibold text-sm text-ink">{slot.label}</p>
              <p className="text-xs text-slate2 mt-0.5 mb-3">{slot.hint}</p>
              <button
                onClick={() => inputRefs.current[slot.key]?.click()}
                disabled={busy === slot.key}
                data-testid={`image-upload-${slot.key}`}
                className="inline-flex items-center gap-2 border border-ink px-4 py-2 font-mono text-[10px] tracking-[0.15em] uppercase text-ink hover:bg-ink hover:text-white disabled:opacity-60 transition-colors"
              >
                {busy === slot.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5 text-gold" />}
                {busy === slot.key ? "Uploading…" : "Change Photo"}
              </button>
              <input
                ref={(el) => (inputRefs.current[slot.key] = el)}
                type="file"
                accept=".jpg,.jpeg,.png,.webp"
                className="hidden"
                data-testid={`image-input-${slot.key}`}
                onChange={(e) => { upload(slot.key, e.target.files?.[0]); e.target.value = ""; }}
              />
              {isCustom(slot.key) && (
                <button
                  onClick={async () => {
                    try {
                      await api.delete(`/admin/images/${slot.key}`);
                      setCustom((c) => (c === null ? customImages : c).filter((k) => k !== slot.key));
                      toast.success("Default photo restored");
                    } catch (e) { toast.error(formatApiError(e)); }
                  }}
                  data-testid={`image-restore-${slot.key}`}
                  className="ml-2 inline-flex items-center gap-2 text-xs text-slate2 hover:text-ink px-2 py-2 transition-colors"
                >
                  Restore default
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
